import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SchemaOrg from "@/components/base/SchemaOrg";

const siteUrl =
  (import.meta.env.VITE_SITE_URL as string) || "https://tbm-lab.genaiweb.ai";

const privacyPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "プライバシーポリシー | 株式会社TBM健康美容研究所",
  "description": "株式会社TBM健康美容研究所のプライバシーポリシー。個人情報の取得・利用目的、第三者提供、安全管理措置、開示・訂正・削除の手続き、お問い合わせ窓口についてご案内しています。",
  "url": `${siteUrl}/privacy`,
  "publisher": {
    "@type": "Organization",
    "name": "株式会社TBM健康美容研究所",
    "url": siteUrl,
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "ホーム",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "プライバシーポリシー",
      item: `${siteUrl}/privacy`,
    },
  ],
};

const sections = [
  {
    title: "1. 個人情報の定義",
    body: "本ポリシーにおける「個人情報」とは、個人情報の保護に関する法律（以下「個人情報保護法」といいます）第2条第1項に定める、生存する個人に関する情報であって、氏名、生年月日、住所、電話番号、メールアドレス、その他の記述等により特定の個人を識別できるものをいいます。",
  },
  {
    title: "2. 個人情報の取得方法",
    body: "当社は、お問い合わせフォーム、電話、ファクシミリ、電子メール、名刺の交換、各種申込書類など、適正かつ公正な手段によって個人情報を取得します。ご本人の同意なく、偽りその他不正の手段により個人情報を取得することはありません。",
  },
  {
    title: "3. 個人情報の利用目的",
    body: "当社は、取得した個人情報を以下の目的の範囲内で利用いたします。",
    list: [
      "お問い合わせ・ご相談への対応および回答のご連絡のため",
      "セミナー・スクール・コンサルティング等の各種サービスのご案内・ご提供のため",
      "総合美容検定の申込受付・運営・結果通知のため",
      "教材・出版、新製品化、健康事業等に関する情報提供のため",
      "契約の履行および請求・支払い等の事務処理のため",
      "当社サービス・ウェブサイトの品質向上のための分析のため",
      "その他、上記に関連・付随する業務の遂行のため",
    ],
  },
  {
    title: "4. 個人情報の第三者提供",
    body: "当社は、法令に基づく場合、人の生命・身体・財産の保護のために必要がある場合、公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合、および国の機関等の事務への協力が必要な場合を除き、あらかじめご本人の同意を得ることなく、個人情報を第三者に提供することはありません。",
  },
  {
    title: "5. 個人情報の安全管理措置",
    body: "当社は、取り扱う個人情報の漏えい、滅失またはき損の防止その他の個人情報の安全管理のために、必要かつ適切な措置を講じます。具体的には、アクセス制御、情報セキュリティ対策の実施、従業者に対する教育・監督、委託先の監督などを行います。",
  },
  {
    title: "6. 個人情報の開示・訂正・削除",
    body: "ご本人から、自己の個人情報について開示・訂正・追加・削除・利用停止等のご請求があった場合は、ご本人であることを確認した上で、法令に基づき合理的な期間および範囲内で対応いたします。",
  },
  {
    title: "7. クッキー（Cookie）の利用",
    body: "当社ウェブサイトでは、サイトの利便性向上やアクセス状況の分析を目的として、クッキーを使用する場合があります。クッキーの取り扱いの詳細については、クッキーポリシーをご確認ください。",
  },
  {
    title: "8. プライバシーポリシーの変更",
    body: "当社は、法令の改正や事業内容の変更等に伴い、本ポリシーを随時改定することがあります。改定した内容は、本ページに掲載した時点から効力を生じるものとします。",
  },
  {
    title: "9. お問い合わせ窓口",
    body: "個人情報の取り扱いに関するお問い合わせは、下記までご連絡ください。",
    contact: {
      company: "株式会社TBM健康美容研究所",
      address: "〒370-0841 群馬県高崎市栄町13-1",
      tel: "027-310-2220",
      fax: "027-327-6344",
    },
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <SchemaOrg data={privacyPageSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <Header />

      <main>
        {/* Page Header */}
        <section className="relative w-full pt-24 md:pt-32 pb-12 md:pb-16 bg-gamo-light-gray">
          <div className="section-container">
            <nav className="flex items-center gap-2 text-xs text-gamo-medium-gray mb-6">
              <a href="/" className="hover:text-gamo-green transition-colors">ホーム</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-gamo-dark-gray">プライバシーポリシー</span>
            </nav>
            <h1 className="text-gamo-black text-2xl md:text-3xl lg:text-4xl font-bold tracking-wide">
              プライバシーポリシー
            </h1>
            <p className="text-gamo-medium-gray text-sm mt-3">
              Privacy Policy
            </p>
            <div className="w-10 h-px bg-gamo-green mt-5" />
          </div>
        </section>

        {/* Content */}
        <section className="w-full bg-white py-12 md:py-16">
          <div className="section-container max-w-3xl">
            <p className="text-gamo-medium-gray text-sm leading-relaxed mb-10">
              株式会社TBM健康美容研究所（以下「当社」といいます）は、お客様の個人情報を適切に取り扱い、保護することが社会的責務であると認識し、個人情報保護方針を以下のとおり定め、これを遵守いたします。
            </p>

            <div className="space-y-10">
              {sections.map((section) => (
                <article key={section.title}>
                  <h2 className="text-gamo-black text-lg md:text-xl font-bold mb-3">
                    {section.title}
                  </h2>
                  <p className="text-gamo-dark-gray text-sm leading-relaxed">
                    {section.body}
                  </p>
                  {section.list && (
                    <ul className="mt-3 space-y-2">
                      {section.list.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-gamo-dark-gray text-sm leading-relaxed"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-gamo-green flex-shrink-0 mt-2"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.contact && (
                    <div className="mt-4 bg-gamo-light-gray border border-gamo-border-gray rounded-lg p-5 md:p-6">
                      <dl className="space-y-2 text-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                          <dt className="text-gamo-medium-gray w-24 flex-shrink-0">名称</dt>
                          <dd className="text-gamo-dark-gray font-medium">{section.contact.company}</dd>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                          <dt className="text-gamo-medium-gray w-24 flex-shrink-0">所在地</dt>
                          <dd className="text-gamo-dark-gray">{section.contact.address}</dd>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                          <dt className="text-gamo-medium-gray w-24 flex-shrink-0">電話番号</dt>
                          <dd className="text-gamo-dark-gray">{section.contact.tel}</dd>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                          <dt className="text-gamo-medium-gray w-24 flex-shrink-0">FAX</dt>
                          <dd className="text-gamo-dark-gray">{section.contact.fax}</dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </article>
              ))}
            </div>

            <p className="text-gamo-medium-gray text-xs leading-relaxed mt-12 pt-6 border-t border-gamo-border-gray">
              制定日：2026年9月17日
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}